import type { Proyecto, Cliente, Servicio, Cotizacion, EstadoProyecto } from '@/lib/types'
import { useSupabase } from '@/hooks/useSupabase'
import { useState, useEffect } from 'react'

interface ProyectoModalProps {
  proyecto: Proyecto | null       // null = create mode
  clientes: Cliente[]
  servicios: Servicio[]
  cotizaciones: Cotizacion[]
  onClose: () => void
  onSaved: () => void
}

const ESTADOS: { value: EstadoProyecto; label: string; emoji: string }[] = [
  { value: 'activo', label: 'Activo', emoji: '' },
  { value: 'pausado', label: 'Pausado', emoji: '' },
  { value: 'completado', label: 'Completado', emoji: '' },
  { value: 'cancelado', label: 'Cancelado', emoji: '' },
]

export default function ProyectoModal({ proyecto, clientes, servicios, cotizaciones: _cotizaciones, onClose, onSaved }: ProyectoModalProps) {
  const db = useSupabase()
  const isEdit = proyecto !== null

  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    cliente_id: 0,
    servicio_id: 0,
    estado: 'activo' as EstadoProyecto,
    fecha_inicio: '',
    fecha_fin_estimada: '',
    moneda: 'USD',
    presupuesto: 0,
    notas: '',
    github_repo: '',
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (proyecto) {
      setForm({
        nombre: proyecto.nombre,
        descripcion: proyecto.descripcion ?? '',
        cliente_id: proyecto.cliente_id,
        servicio_id: proyecto.servicio_id ?? 0,
        estado: proyecto.estado,
        fecha_inicio: proyecto.fecha_inicio ?? '',
        fecha_fin_estimada: proyecto.fecha_fin_estimada ?? '',
        moneda: proyecto.moneda,
        presupuesto: proyecto.presupuesto,
        notas: proyecto.notas ?? '',
        github_repo: proyecto.github_repo ?? '',
      })
    }
  }, [proyecto])

  function setField(field: string, value: string | number) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function handleSave() {
    if (!form.nombre.trim() || !form.cliente_id) return
    setSaving(true)
    try {
      const record: Record<string, unknown> = {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim() || null,
        cliente_id: form.cliente_id,
        servicio_id: form.servicio_id || null,
        estado: form.estado,
        fecha_inicio: form.fecha_inicio || null,
        fecha_fin_estimada: form.fecha_fin_estimada || null,
        moneda: form.moneda,
        presupuesto: form.presupuesto,
        notas: form.notas.trim() || null,
        github_repo: form.github_repo.trim() || null,
      }
      if (isEdit) {
        record.id = proyecto!.id
        await db.update('proyectos', record)
      } else {
        await db.add('proyectos', record)
      }
      onSaved()
    } catch (err) {
      console.error('Error saving project:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} id="proyecto-modal-overlay">
      <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Editar Proyecto' : 'Nuevo Proyecto'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {/* Nombre */}
          <div className="form-group">
            <label className="form-label">Nombre del Proyecto *</label>
            <input
              className="form-input"
              id="proyecto-nombre"
              value={form.nombre}
              onChange={e => setField('nombre', e.target.value)}
              placeholder="Ej: Migración Cloud AWS"
              required
            />
          </div>

          {/* Cliente + Estado */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Cliente *</label>
              <select
                className="form-select"
                id="proyecto-cliente"
                value={form.cliente_id}
                onChange={e => setField('cliente_id', Number(e.target.value))}
              >
                <option value={0}>— Seleccionar cliente —</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.empresa}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Estado</label>
              <select
                className="form-select"
                id="proyecto-estado"
                value={form.estado}
                onChange={e => setField('estado', e.target.value)}
              >
                {ESTADOS.map(e => (
                  <option key={e.value} value={e.value}>{e.emoji} {e.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Servicio */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Servicio asociado</label>
              <select
                className="form-select"
                id="proyecto-servicio"
                value={form.servicio_id}
                onChange={e => setField('servicio_id', Number(e.target.value))}
              >
                <option value={0}>— Sin servicio —</option>
                {servicios.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Cotizaciones</label>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '6px 0 0' }}>
                Las cotizaciones se gestionan desde la vista de detalle del proyecto.
              </p>
            </div>
          </div>

          {/* Fechas */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Fecha de inicio</label>
              <input
                className="form-input"
                id="proyecto-fecha-inicio"
                type="date"
                value={form.fecha_inicio}
                onChange={e => setField('fecha_inicio', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Fecha fin estimada</label>
              <input
                className="form-input"
                id="proyecto-fecha-fin"
                type="date"
                value={form.fecha_fin_estimada}
                onChange={e => setField('fecha_fin_estimada', e.target.value)}
              />
            </div>
          </div>

          {/* Moneda + Presupuesto */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Moneda</label>
              <select
                className="form-select"
                id="proyecto-moneda"
                value={form.moneda}
                onChange={e => setField('moneda', e.target.value)}
              >
                <option value="USD">USD</option>
                <option value="PEN">PEN</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Presupuesto</label>
              <input
                className="form-input"
                id="proyecto-presupuesto"
                type="number"
                min="0"
                step="0.01"
                value={form.presupuesto}
                onChange={e => setField('presupuesto', Number(e.target.value))}
              />
            </div>
          </div>

          {/* Descripción */}
          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea
              className="form-textarea"
              id="proyecto-descripcion"
              value={form.descripcion}
              onChange={e => setField('descripcion', e.target.value)}
              placeholder="Descripción breve del proyecto..."
            />
          </div>

          {/* Notas */}
          <div className="form-group">
            <label className="form-label">Notas internas</label>
            <textarea
              className="form-textarea"
              id="proyecto-notas"
              value={form.notas}
              onChange={e => setField('notas', e.target.value)}
              placeholder="Notas internas..."
              style={{ minHeight: 60 }}
            />
          </div>

          {/* Repositorio GitHub */}
          <div className="form-group">
            <label className="form-label">
              Repositorio GitHub
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, fontSize: '0.8rem' }}>
                formato: owner/repo
              </span>
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="form-input"
                id="proyecto-github-repo"
                value={form.github_repo}
                onChange={e => setField('github_repo', e.target.value)}
                placeholder="Ej: futura-wealth/platform"
                style={{ flex: 1 }}
              />
              {form.github_repo && (
                <a
                  href={`https://github.com/${form.github_repo}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', textDecoration: 'none' }}
                >
                  ↗ Ver en GitHub
                </a>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button
              className="btn btn-primary"
              id="proyecto-save-btn"
              onClick={handleSave}
              disabled={saving || !form.nombre.trim() || !form.cliente_id}
            >
              {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Proyecto'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
